import React, { useEffect, useRef, useState } from "react";
import { petAPI, clinicAPI, inpatientAPI, authUtils } from "../../services/api";
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import SelectPaymentModal from "./SelectPaymentModal";
import { useTranslation } from "react-i18next";
import SpecialRequestsNoteModal from "./SpecialRequestsNoteModal";

const EditInpatientModal = ({
  isOpen,
  onClose,
  onSave,
  vets = [],
  workingHours = [],
  editData = null,
  fetchAllTabData,
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [petOwnerSearch, setPetOwnerSearch] = useState("");
  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [petOwnerPets, setPetOwnerPets] = useState([]);
  const [isLoadingPets, setIsLoadingPets] = useState(false);
  const [petOwnerSearchResults, setPetOwnerSearchResults] = useState([]);
  const [showPetOwnerDropdown, setShowPetOwnerDropdown] = useState(false);
  const [isSearchingPetOwners, setIsSearchingPetOwners] = useState(false);
  const [vetSearch, setVetSearch] = useState("");
  const [selectedVet, setSelectedVet] = useState(null);
  const [showVetDropdown, setShowVetDropdown] = useState(false);
  const [isSavingInpatient, setIsSavingInpatient] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [downpayment, setDownpayment] = useState(null);
  const [lengthOfStayDays, setLengthOfStayDays] = useState(0);
  const [editInpatient, setEditInpatient] = useState({
    id: null,
    pet_owner: "",
    pet: "",
    vet: "",
    reason: "",
    date: "",
    time: "",
    cage_no: "",
    allowed_food: "",
    allowed_fluids: "",
  });
  const [ratePerDay, setRatePerDay] = useState(0);
  const [isIsolated, setIsIsolated] = useState(false);

  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);

  const [specialRequestsNote, setSpecialRequestsNote] = useState("");
  const [showSpecialRequestsModal, setShowSpecialRequestsModal] = useState(false);

  // Payment modal state
  const [showSelectPaymentModal, setShowSelectPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(editData?.payment_type_id);
  const [paymentNotes, setPaymentNotes] = useState(editData?.payments_note || "");

  const timeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const generateTimeSlots = (hours) => {
    if (!hours || hours.length === 0) return [];
    const slots = [];
    hours.forEach(wh => {
      const startTime = wh.opening_time || wh.from;
      const endTime = wh.closing_time || wh.to;
      if (!startTime || !endTime) return;
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const t = minutesToTime(minutes);
        slots.push({ time: t, display: t, day: (wh.day || '').toString().toLowerCase() });
      }
    });
    return slots.sort((a, b) => a.time.localeCompare(b.time));
  };

  useEffect(() => {
    const slots = generateTimeSlots(workingHours);
    setAvailableTimeSlots(slots);
  }, [workingHours]);

  useEffect(() => {
    if (selectedPetOwner) {
      const petsFromSearch = selectedPetOwner.pets?.list || selectedPetOwner.pets || [];
      if (petsFromSearch.length > 0) {
        setPetOwnerPets(petsFromSearch);
        if (petsFromSearch.length === 1 && !selectedPet) {
          setSelectedPet(petsFromSearch[0]);
        }
        setIsLoadingPets(false);
      } else {
        setIsLoadingPets(true);
        petAPI.getOtherPets(selectedPetOwner.id)
          .then((res) => {
            const pets = res.data?.data || [];
            setPetOwnerPets(pets);
            if (pets.length === 1 && !selectedPet) {
              setSelectedPet(pets[0]);
            }
          })
          .catch(() => {
            setPetOwnerPets([]);
            setSelectedPet(null);
          })
          .finally(() => {
            setIsLoadingPets(false);
          });
      }
    } else {
      setPetOwnerPets([]);
      setSelectedPet(null);
      setIsLoadingPets(false);
    }
  }, [selectedPetOwner]);

  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) return availableTimeSlots;
    const selectedDay = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    const isToday = selectedDateObj.toDateString() === today.toDateString();
    let filtered = availableTimeSlots.filter(slot => slot.day === selectedDay);
    if (isToday) {
      const nowMin = today.getHours() * 60 + today.getMinutes();
      filtered = filtered.filter(slot => {
        const [h, m] = slot.time.split(':').map(Number);
        return (h * 60 + m) >= (nowMin + 15);
      });
    }
    return filtered;
  };

  useEffect(() => {
    if (isOpen && dateInputRef.current) {
      if (datePickerRef.current) datePickerRef.current.destroy();

      const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
      const availableDays = (workingHours || []).map(wh => dayMap[String(wh.day || '').toLowerCase()]).filter((d) => d !== undefined);

      const disableFunction = (workingHours || []).length > 0 ? [
        function (date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) return true;
          const dayOfWeek = date.getDay();
          return !availableDays.includes(dayOfWeek);
        }
      ] : [
        function (date) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return date < today;
        }
      ];

      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        disable: disableFunction,
        onChange: function (selectedDates, dateStr) {
          setEditInpatient(prev => ({ ...prev, date: dateStr, time: "" }));
          setShowTimeDropdown(false);
        }
      });

      setTimeout(() => {
        if (editInpatient.date && datePickerRef.current) {
          datePickerRef.current.setDate(editInpatient.date);
        }
      }, 80);
    }

    return () => {
      if (datePickerRef.current) datePickerRef.current.destroy();
    };
  }, [isOpen, workingHours, editInpatient.date]);

  useEffect(() => {
    if (isOpen && editData?.id) {
      inpatientAPI.getInpatientVisit(editData.id)
        .then(response => {
          const fullData = response.data?.data;
          if (fullData) {
            setEditInpatient(prev => ({
              ...prev,
              allowed_food: fullData.feeding_order || fullData.allowed_food || prev.allowed_food,
              allowed_fluids: fullData.water_order || fullData.allowed_fluids || prev.allowed_fluids,
              cage_no: fullData.cage_no || prev.cage_no,
              reason: fullData.reason || prev.reason,
            }));

            const rpd = fullData.rate_per_day ?? fullData.originalData?.rate_per_day;
            if (rpd !== undefined && rpd !== null) setRatePerDay(Number(rpd) || 0);

            const isolated = fullData.is_isolated ?? fullData.originalData?.is_isolated;
            if (isolated !== undefined && isolated !== null) setIsIsolated(!!isolated);

            if (fullData.length_of_stay_days !== undefined && fullData.length_of_stay_days !== null) {
              setLengthOfStayDays(Number(fullData.length_of_stay_days) || 0);
            }

            const dpCandidates = [fullData.downpayment, fullData.down_payment, fullData.originalData?.downpayment, fullData.originalData?.down_payment];
            const dpFirst = dpCandidates.find((v) => v !== undefined && v !== null && v !== "");
            if (dpFirst !== undefined) setDownpayment(Number(dpFirst) || 0);

            if (fullData.vet) {
              const v = fullData.vet;
              const displayName = v.user?.name || v.name || ((v.first_name || '') + ' ' + (v.last_name || '')).trim();
              setSelectedVet({ ...v, user_id: v.user_id || v.id, name: displayName });
              setVetSearch(displayName);
            }
          }
        })
        .catch(err => console.error("Error fetching full inpatient details:", err));
    }
  }, [isOpen, editData?.id]);

  useEffect(() => {
    if (isOpen && editData) {
      let date = editData.date || "";
      let time = editData.time || "";
      if (editData.originalData?.visit_date) {
        try {
          const dt = new Date(editData.originalData.visit_date);
          const yyyy = dt.toISOString().split('T')[0];
          const hh = dt.getHours().toString().padStart(2, '0');
          const mm = dt.getMinutes().toString().padStart(2, '0');
          date = date || yyyy;
          time = time || `${hh}:${mm}`;
        } catch (_) { }
      }

      setEditInpatient({
        id: editData.id,
        pet_owner: editData.pet_owner || "",
        pet: typeof editData.pet === 'string' ? editData.pet : (editData.pet?.name || ""),
        vet: editData.vet || "",
        reason: editData.reason || "",
        date,
        time,
        cage_no: editData.cage_no || "",
        allowed_food: editData.feeding_order || editData.allowed_food || editData.originalData?.feeding_order || editData.originalData?.allowed_food || "",
        allowed_fluids: editData.water_order || editData.allowed_fluids || editData.originalData?.water_order || editData.originalData?.allowed_fluids || "",
      });

      setRatePerDay(Number(editData.rate_per_day ?? editData.originalData?.rate_per_day ?? 0));
      setIsIsolated(!!(editData.is_isolated ?? editData.originalData?.is_isolated ?? false));

      const dpCandidates = [editData.downpayment, editData.down_payment, editData.originalData?.downpayment, editData.originalData?.down_payment];
      const dpFirst = dpCandidates.find((v) => v !== undefined && v !== null && v !== "");
      setDownpayment(Number(dpFirst) || 0);

      setLengthOfStayDays(Number(editData.length_of_stay_days) || 0);
      setPaymentNotes(editData.payments_note || editData.originalData?.payments_note || "");

      if (editData.originalData?.pet_owner) {
        setSelectedPetOwner(editData.originalData.pet_owner);
        setPetOwnerSearch(editData.pet_owner);
      }
      if (editData.originalData?.pet) setSelectedPet(editData.originalData.pet);
      if (editData.originalData?.vet) {
        const v = editData.originalData.vet;
        const displayName = v.user?.name || v.name || ((v.first_name || '') + ' ' + (v.last_name || '')).trim();
        setSelectedVet({ ...v, user_id: v.user_id || v.id });
        setVetSearch(displayName);
      }
      setSpecialRequestsNote(
        editData.special_requests_note ||
        editData.originalData?.special_requests_note ||
        ""
      );
    } else if (isOpen && !editData) {
      setEditInpatient({ id: null, pet_owner: "", pet: "", vet: "", reason: "", date: "", time: "", cage_no: "", allowed_food: "", allowed_fluids: "" });
      setDownpayment(null);
      setLengthOfStayDays(0);
      setRatePerDay(0);
      setIsIsolated(false);
      setSpecialRequestsNote("");
      setPetOwnerSearch("");
      setSelectedPetOwner(null);
      setPetOwnerPets([]);
      setVetSearch("");
      setSelectedVet(null);
      setSelectedPet(null);
      setShowTimeDropdown(false);
    }
  }, [isOpen, editData]);

  const handleSaveClick = async () => {
    if (!selectedPetOwner) { showErrorToast(t("receptionInpatients.admit.validation.petGuardian")); return; }
    if (!selectedPet) { showErrorToast(t("receptionInpatients.admit.validation.pet")); return; }
    if (!selectedVet) { showErrorToast(t("receptionInpatients.admit.validation.vet")); return; }
    if (!editInpatient.date) { showErrorToast(t("receptionInpatients.admit.validation.date")); return; }
    if (!editInpatient.time) { showErrorToast(t("receptionInpatients.admit.validation.time")); return; }

    try {
      setIsSavingInpatient(true);
      const visitDateTime = `${editInpatient.date} ${editInpatient.time}:00`;
      const payload = {
        pet_owner_id: selectedPetOwner.id,
        pet_id: selectedPet.id,
        vet_id: selectedVet.user_id,
        visit_date: visitDateTime,
        reason: editInpatient.reason,
        cage_no: editInpatient.cage_no,
        feeding_order: editInpatient.allowed_food,
        water_order: editInpatient.allowed_fluids,
        is_isolated: isIsolated,
        rate_per_day: ratePerDay,
        downpayment: downpayment || 0,
        payment_type_id: selectedPaymentMethod,
        payment_note: paymentNotes
      };

      if (lengthOfStayDays > 0) payload.length_of_stay_days = lengthOfStayDays;
      if (specialRequestsNote && specialRequestsNote.trim()) payload.special_requests_note = specialRequestsNote.trim();

      await inpatientAPI.updateInpatientVisit(editInpatient.id, payload);
      showSuccessToast(t("receptionInpatients.admit.toasts.updateSuccess"));

      const updatedRow = {
        id: editInpatient.id,
        pet_owner: selectedPetOwner.display_name || selectedPetOwner.full_name || `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}`,
        pet: selectedPet.name,
        vet: selectedVet.user?.name || selectedVet.name || `${selectedVet.first_name || ''} ${selectedVet.last_name || ''}`.trim(),
        date: editInpatient.date,
        time: editInpatient.time,
        status: editData?.status || 'admitted',
        originalData: { ...editData?.originalData, ...payload, pet_owner: selectedPetOwner, pet: selectedPet, vet: selectedVet }
      };

      if (onSave) onSave(updatedRow);
      onClose();
    } catch (error) {
      showErrorToast(error.response?.data?.message || t("receptionInpatients.admit.toasts.updateFailed"));
    } finally {
      setIsSavingInpatient(false);
      if (fetchAllTabData) fetchAllTabData(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="reception-modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)'
    }}>
      <div className="reception-modal" style={{
        backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        width: '90%', maxWidth: '800px', height: '95vh', maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'
      }}>
        <div className="reception-modal-header" style={{
          padding: '24px 32px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexDirection: isRTL ? 'row-reverse' : 'row', background: 'linear-gradient(to right, #ffffff, #f9fafb)'
        }}>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '28px' }}>✏️</span> {t("receptionInpatients.admit.header.edit")}
          </h2>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div className="reception-modal-form" style={{ padding: '24px 32px', overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>👤 {t("receptionInpatients.admit.fields.petGuardian.label")}</span>
                <div style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px', backgroundColor: '#f9fafb', color: '#111827' }}>
                  {selectedPetOwner ? (selectedPetOwner.display_name || selectedPetOwner.full_name || `${selectedPetOwner.first_name} ${selectedPetOwner.last_name}`) : ""}
                </div>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>🐾 {t("receptionInpatients.admit.fields.pet.label")}</span>
                <select className="reception-modal-input" value={selectedPet?.name || ""} onChange={(e) => setSelectedPet(petOwnerPets.find(p => p.name === e.target.value))} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px' }}>
                  {petOwnerPets.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>👨‍⚕️ {t("receptionInpatients.admit.fields.vet.label")}</span>
                <div style={{ position: 'relative' }}>
                  <div onClick={() => setShowVetDropdown(!showVetDropdown)} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{selectedVet ? (selectedVet.user?.name || selectedVet.name) : ""}</span>
                    <span>▼</span>
                  </div>
                  {showVetDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', zIndex: 10, maxHeight: '200px', overflow: 'auto' }}>
                      {vets.map(v => <div key={v.user_id} onClick={() => { setSelectedVet(v); setShowVetDropdown(false); }} style={{ padding: '12px', cursor: 'pointer' }}>{v.user?.name || v.name}</div>)}
                    </div>
                  )}
                </div>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>📝 {t("receptionInpatients.admit.fields.reason.label")}</span>
                <input type="text" value={editInpatient.reason} onChange={(e) => setEditInpatient({ ...editInpatient, reason: e.target.value })} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px' }} />
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>🍖 {t("receptionInpatients.admit.fields.feedingOrder.label")}</span>
                <select value={editInpatient.allowed_food} onChange={(e) => setEditInpatient({ ...editInpatient, allowed_food: e.target.value })} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px', backgroundColor: 'white' }}>
                  <option value="">Select...</option>
                  <option value="allowed">Allowed</option>
                  <option value="not_allowed">Not Allowed</option>
                  <option value="special">Special</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>💧 {t("receptionInpatients.admit.fields.waterOrder.label")}</span>
                <select value={editInpatient.allowed_fluids} onChange={(e) => setEditInpatient({ ...editInpatient, allowed_fluids: e.target.value })} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px', backgroundColor: 'white' }}>
                  <option value="">Select...</option>
                  <option value="allowed">Allowed</option>
                  <option value="not_allowed">Not Allowed</option>
                  <option value="special">Special</option>
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>💰 {t("receptionInpatients.admit.fields.ratePerDay.label")}</span>
                <input type="number" value={ratePerDay} onChange={(e) => setRatePerDay(Number(e.target.value))} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#4b5563' }}>💵 {t("receptionInpatients.admit.fields.downpayment.label")}</span>
                <input type="number" value={downpayment} onChange={(e) => setDownpayment(Number(e.target.value))} style={{ padding: '14px 18px', border: '2px solid #f3f4f6', borderRadius: '12px' }} />
              </label>
            </div>


          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', paddingTop: '24px', borderTop: '1px solid #f3f4f6', background: 'white' }}>
            <button
              onClick={onClose}
              style={{
                padding: '12px 28px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white',
                color: '#4b5563',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={() => setShowSpecialRequestsModal(true)}
              style={{
                padding: '12px 28px',
                border: '1.5px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: 'white',
                color: '#4b5563',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '18px' }}>📋</span> {t("receptionInpatients.admit.buttons.specialRequests", { defaultValue: "Special Requests" })}
            </button>
            <button
              onClick={handleSaveClick}
              disabled={isSavingInpatient}
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: '12px',
                background: isSavingInpatient ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isSavingInpatient ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isSavingInpatient ? 'none' : '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                padding: '12px 36px'
              }}
              onMouseEnter={(e) => {
                if (!isSavingInpatient) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 15px -3px rgba(59, 130, 246, 0.3), 0 4px 6px -2px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'brightness(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSavingInpatient) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)';
                  e.target.style.filter = 'none';
                }
              }}
            >
              {isSavingInpatient ? (
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
              ) : (
                <span style={{ fontSize: '18px' }}>💾</span>
              )}
              {isSavingInpatient ? "Saving..." : t("receptionInpatients.admit.buttons.save")}
            </button>
          </div>
        </div>

        <SpecialRequestsNoteModal
          isOpen={showSpecialRequestsModal}
          onClose={() => setShowSpecialRequestsModal(false)}
          value={specialRequestsNote}
          onSave={(note) => setSpecialRequestsNote(note)}
          onPersist={async (note) => {
            if (!editInpatient?.id) return;
            await inpatientAPI.updateInpatientVisit(editInpatient.id, {
              special_requests_note: (note || '').trim(),
            });
            if (fetchAllTabData) fetchAllTabData(true);
          }}
        />
      </div>
    </div>
  );
};

export default EditInpatientModal;
